import React from "react";
import { useTranslation } from "react-i18next";
import { Hourglass, Link as LinkIcon, Check } from "lucide-react";
import { Button } from "../ui/button";
import type { EngagementRanked } from "../../lib/insights/engagement";

export interface PendingParticipantsProps {
  participants: EngagementRanked[];
  /** True if the organizer has already pressed "Copy link" for this id */
  isShared: (participantId: string) => boolean;
  /** Click handler — copies the poll link AND marks as shared */
  onCopyLink: (participantId: string) => void;
  /** Top-level "Copy link to share" handler (no participant) */
  onCopyShareLink?: () => void;
}

const PendingParticipants: React.FC<PendingParticipantsProps> = ({
  participants,
  isShared,
  onCopyLink,
  onCopyShareLink,
}) => {
  const { t } = useTranslation();
  if (participants.length === 0) return null;
  return (
    <section
      className="rounded-2xl border border-border bg-card/60 p-3 space-y-2"
      data-testid="pending-section"
      aria-labelledby="pending-heading"
    >
      <header className="flex items-center justify-between gap-3">
        <h3
          id="pending-heading"
          className="flex items-center gap-2 text-sm font-medium text-muted-foreground"
        >
          <Hourglass className="w-4 h-4" aria-hidden />
          {t("pending_waiting")}
          <span data-testid="pending-count">({participants.length})</span>
        </h3>
        {onCopyShareLink && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onCopyShareLink}
            data-testid="pending-copy-share-link"
            className="gap-1"
          >
            <LinkIcon className="w-3.5 h-3.5" aria-hidden />
            {t("copy_link_to_share")}
          </Button>
        )}
      </header>
      <p className="text-xs text-muted-foreground/80 -mt-1">
        {t("not_voted_share_hint")}
      </p>
      <ul className="divide-y divide-border/60 opacity-[0.55]">
        {participants.map((p) => {
          const shared = isShared(p.id);
          return (
            <li
              key={p.id}
              data-testid={`pending-row-${p.id}`}
              data-shared={shared ? "true" : undefined}
              className="flex items-center justify-between gap-3 py-1.5"
            >
              <span
                className="text-sm text-foreground truncate flex items-center gap-1.5"
                title={p.name}
              >
                {shared && (
                  <span
                    data-testid={`pending-shared-check-${p.id}`}
                    aria-label={t("link_already_copied")}
                    className="inline-flex"
                  >
                    <Check className="w-3.5 h-3.5 text-success" />
                  </span>
                )}
                {p.name}
              </span>
              <Button
                type="button"
                variant={shared ? "outline" : "ghost"}
                size="sm"
                onClick={() => onCopyLink(p.id)}
                data-testid={`pending-copy-${p.id}`}
                data-state={shared ? "shared" : "idle"}
                className="gap-1"
              >
                <LinkIcon className="w-3.5 h-3.5" aria-hidden />
                {shared ? t("recopy_link") : t("copy_link")}
              </Button>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export default PendingParticipants;
